import type { CampaignWithComputed } from './campaign';
import {
  isPaymentReady,
  getNetPayableAmount,
  getPayableOverrides,
  deriveInfluencerInvoiceStatus,
  getActualInfluencerPaidAmount,
  parseInfluencerInvoices,
} from '../utils/paymentCalculations';

export type PaymentCategory = 'active' | 'ready' | 'forecast' | 'paid';

export interface PaymentCategoryConfig {
  key: PaymentCategory;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

export const PAYMENT_CATEGORIES: Record<PaymentCategory, PaymentCategoryConfig> = {
  active: {
    key: 'active',
    label: 'Campanhas ativas',
    description: 'Campanhas em curso à espera do pagamento do cliente',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  ready: {
    key: 'ready',
    label: 'Prontas a pagar',
    description: 'O cliente pagou: o criador pode ser pago',
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  forecast: {
    key: 'forecast',
    label: 'Previsão',
    description: 'Campanhas em consulta',
    color: 'amber',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
  paid: {
    key: 'paid',
    label: 'Pagas',
    description: 'O criador já foi pago',
    color: 'slate',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-600',
    borderColor: 'border-slate-200',
  },
};

export interface InfluencerPaymentSummary {
  influencer: string;
  campaigns: {
    active: CampaignWithComputed[];
    ready: CampaignWithComputed[];
    forecast: CampaignWithComputed[];
    paid: CampaignWithComputed[];
  };
  totals: {
    active: number;
    ready: number;
    forecast: number;
    paid: number;
  };
  counts: {
    active: number;
    ready: number;
    forecast: number;
    paid: number;
  };
}

export interface PaymentStats {
  totalActive: number;
  totalReady: number;
  totalForecast: number;
  totalPaid: number;
  countActive: number;
  countReady: number;
  countForecast: number;
  countPaid: number;
  influencerCount: number;
}

export function categorizePayment(campaign: CampaignWithComputed): PaymentCategory {
  const derivedStatus = deriveInfluencerInvoiceStatus(campaign);
  if (derivedStatus === 'Paid') {
    return 'paid';
  }

  // "Ready" means there is money to hand over now — the same rule as the
  // dashboard card. A campaign whose first tranche is already paid out, with
  // the client's next tranche still pending, is waiting, not ready.
  if (isPaymentReady(campaign)) {
    return 'ready';
  }

  if (campaign.campaign_status === 'Consultation') {
    return 'forecast';
  }

  return 'active';
}

export function getNetReceivedAmount(campaign: CampaignWithComputed): number {
  // The same payable the dashboard and Pagamentos list use
  return getNetPayableAmount(campaign, getPayableOverrides(campaign));
}

/** What has actually been handed to the creator so far (sum of paid tranches). */
export function getCreatorPaidOutAmount(campaign: CampaignWithComputed): number {
  const fromPayables = getActualInfluencerPaidAmount(campaign);
  if (fromPayables > 0) return fromPayables;
  // Legacy rows keep a single status/amount at campaign level
  if (parseInfluencerInvoices(campaign).length === 0 && campaign.influencer_invoice_status === 'Paid') {
    return Number(campaign.influencer_amount_paid) || campaign.net_fee_influencer || 0;
  }
  return 0;
}

/** Date of the most recent tranche paid to the creator. */
export function getLastCreatorPaymentDate(campaign: CampaignWithComputed): string | null {
  const dates = parseInfluencerInvoices(campaign)
    .filter((p) => p.status === 'Paid')
    .map((p) => p.payment_date || p.payment_received_date || '')
    .filter(Boolean)
    .sort();
  return dates.pop() || campaign.influencer_payment_date || null;
}

/**
 * The figure a campaign contributes to a category. A campaign paid in
 * tranches is listed under Paid for what has gone out and under its
 * remaining category for what is still owed, so the two never double count.
 */
export function getPaymentCategoryAmount(campaign: CampaignWithComputed, category: PaymentCategory): number {
  switch (category) {
    case 'paid':
      return getCreatorPaidOutAmount(campaign);
    case 'ready':
      return getNetReceivedAmount(campaign);
    default:
      return Math.max(0, (campaign.net_fee_influencer || 0) - getCreatorPaidOutAmount(campaign));
  }
}

export function groupCampaignsByInfluencer(
  campaigns: CampaignWithComputed[]
): InfluencerPaymentSummary[] {
  const grouped: Record<string, InfluencerPaymentSummary> = {};

  campaigns.forEach((campaign) => {
    const influencer = campaign.influencer;
    const category = categorizePayment(campaign);

    if (!grouped[influencer]) {
      grouped[influencer] = {
        influencer,
        campaigns: { active: [], ready: [], forecast: [], paid: [] },
        totals: { active: 0, ready: 0, forecast: 0, paid: 0 },
        counts: { active: 0, ready: 0, forecast: 0, paid: 0 },
      };
    }

    const summary = grouped[influencer];
    const place = (cat: PaymentCategory) => {
      summary.campaigns[cat].push(campaign);
      summary.totals[cat] += getPaymentCategoryAmount(campaign, cat);
      summary.counts[cat]++;
    };
    place(category);
    // A tranche already paid out belongs in Paid even while the rest is pending
    if (category !== 'paid' && getCreatorPaidOutAmount(campaign) > 0) place('paid');
  });

  return Object.values(grouped).sort((a, b) =>
    a.influencer.localeCompare(b.influencer)
  );
}

export function calculatePaymentStats(
  summaries: InfluencerPaymentSummary[]
): PaymentStats {
  return summaries.reduce(
    (acc, summary) => ({
      totalActive: acc.totalActive + summary.totals.active,
      totalReady: acc.totalReady + summary.totals.ready,
      totalForecast: acc.totalForecast + summary.totals.forecast,
      totalPaid: acc.totalPaid + summary.totals.paid,
      countActive: acc.countActive + summary.counts.active,
      countReady: acc.countReady + summary.counts.ready,
      countForecast: acc.countForecast + summary.counts.forecast,
      countPaid: acc.countPaid + summary.counts.paid,
      influencerCount: summaries.length,
    }),
    {
      totalActive: 0,
      totalReady: 0,
      totalForecast: 0,
      totalPaid: 0,
      countActive: 0,
      countReady: 0,
      countForecast: 0,
      countPaid: 0,
      influencerCount: 0,
    }
  );
}
